import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.iterator.constructor.js";
import "core-js/modules/es.iterator.filter.js";
import "core-js/modules/es.iterator.map.js";
import { computed, ref, watch } from 'vue';
import { UploadOutlined } from '@ant-design/icons-vue';
import URLS, { apiHost } from '@/server/urls';
import { useAuthorStore } from '@/stores';
import { modal } from '@/share/components';
import { api } from '@/server';
import { formatFileSize } from '@/utils';

import { getAPI, fetchApi } from '/Users/ytx/ytx/projects/jxpj/web/jxpj-client/src/server/auto-import.js';
export default {
  name: 'CommonUpload',
  components: {
    UploadOutlined
  },
  props: {
    title: {
      type: String,
      default: '上传'
    },
    multiple: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      default: 'file'
    },
    directory: {
      type: Boolean,
      default: false
    },
    accept: {
      type: String,
      default: ''
    },
    maxCount: {
      type: Number,
      default: 1
    },
    showUploadList: {
      type: Boolean,
      default: true
    },
    value: {
      type: Array,
      default: () => []
    },
    filter: {
      type: Array,
      default: () => ['.jpg', '.jpeg', '.png', '.gif', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx']
    },
    maxSize: {
      type: Number,
      default: 10 * 1024 * 1024
    },
    // 10M
    listType: {
      type: String,
      default: 'text',
      validator: v => ['text', 'picture', 'picture-card'].includes(v)
    }
  },
  emits: ['update:value', 'uploadSuccess', 'uploadError', 'download', 'preview', 'remove'],
  setup(props, {
    emit
  }) {
    const uploadFile = getAPI(api.common.upload, {
      showError: false
    });
    const {
      runAsync: delUploadTempFile
    } = fetchApi(api.common.delUpload);
    const {
      runAsync: delUploadFile
    } = fetchApi(api.common.delFile);
    const authorStore = useAuthorStore();
    const loading = ref(false);
    const fileList = ref([]);
    watch(() => props.value, val => {
      fileList.value = val || [];
    }, {
      deep: true,
      immediate: true
    });
    const postData = ref({});
    const postHeaders = computed(() => {
      return {
        'x-access-token': authorStore.token
      };
    });
    const canUpload = computed(() => {
      return fileList.value.length < props.maxCount;
    });
    const actionUrl = ref(URLS.common.upload);
    const handleRemove = async file => {
      const {
        status,
        action,
        fileId
      } = file || {};
      if (status === 'done' && action === 'upload') {
        // 删除上传的临时文件
        const {
          error
        } = await delUploadTempFile({
          filename: file.name,
          extra: file.uid
        });
        if (error) {
          modal.message.error(error);
          return false;
        }
      } else if (fileId) {
        const confirm = await modal.confirm('删除确认', `确认要删除文件[${file.name}]吗?删除后不可恢复`);
        if (!confirm) return false;
        // 删除服务端文件
        const {
          error
        } = await delUploadFile({
          fileId
        });
        if (error) {
          modal.notification.error(error, '删除文件').catch();
          return false;
        }
        modal.notification.success(`删除文件[${file.name}]成功`, '删除文件');
        emit('remove', file);
      }
      // 其他情况，直接删除
      return true; // 返回false表示不删除文件
    };
    const handleDownload = file => {
      const {
        url
      } = file || {};
      if (!url) return;
      emit('download', file);
      try {
        let targetUrl;
        if (url.indexOf('?') === -1) {
          targetUrl = `${url}?action=download`;
        } else {
          targetUrl = `${url}&action=download`;
        }
        window.open(targetUrl, '_blank');
      } catch (e) {}
    };
    const handlePreview = file => {
      const {
        url
      } = file || {};
      if (!url) return;
      emit('preview', file);
      try {
        window.open(url, '_blank');
      } catch (e) {}
    };
    const handleChange = info => {
      const {
        file
      } = info || {};
      if (!file) return;
      const {
        status,
        error,
        response
      } = file; // status: uploading done error removed
      if (status === 'uploading') {
        loading.value = true;
        return;
      }
      if (status === 'done') {
        // 上传完成
        loading.value = false;
        const idx = fileList.value.slice().findIndex(item => item.uid === file.uid);
        if (idx === -1) return;
        const rows = fileList.value.slice();
        const resFile = {
          ...response,
          uid: file.uid,
          action: 'upload',
          status: 'done'
        };
        rows[idx] = resFile;
        fileList.value = rows;
        const uploadFiles = rows.map(t => {
          const {
            status,
            action,
            ...info
          } = t;
          if (status === 'done' && action === 'upload') {
            return {
              ...info
            };
          }
          return null;
        }).filter(t => !!t);
        emit('uploadSuccess', resFile, uploadFiles);
        emit('update:value', fileList.value);
        return;
      }
      if (status === 'removed') {
        const rows = fileList.value.slice().filter(v => v.status !== 'removed');
        fileList.value = rows;
        emit('update:value', rows);
      }
      if (status === 'error') {
        loading.value = false;
        emit('uploadError', file, error);
      }
    };
    const maxSizeStr = computed(() => {
      return formatFileSize(props.maxSize);
    });

    // 上传文件验证
    const beforeUpload = file => {
      const {
        name,
        size
      } = file || {};
      if (!name) return false;
      const ext = name.substr(name.lastIndexOf('.')); // 获取文件后缀
      const allowExtensions = props.filter || [];
      if (allowExtensions.length > 0 && allowExtensions.findIndex(t => t === ext) === -1) {
        file.status = 'error';
        file.error = `不支持上传${ext}格式的文件`;
        modal.notification.error(file.error, '上传文件');
        return false;
      }
      // 文件大小限制
      if (props.maxSize > 0 && size > props.maxSize) {
        file.status = 'error';
        file.error = `文件大小不能超过${maxSizeStr.value}`;
        modal.notification.error(file.error, '上传文件');
        return false;
      }
      return true;
    };
    // 自定义上传方法
    const customRequest = options => {
      const {
        data,
        headers,
        filename
      } = options || {};
      if (!options.file) return;
      uploadFile({
        data,
        files: [{
          key: filename,
          file: options.file,
          name: options.file.name
        }],
        headers
      }).then(res => {
        const {
          error,
          url,
          ...data
        } = res || {};
        if (error) {
          options.onError(error);
          return;
        }
        options.onSuccess({
          ...data,
          url: `${url ? apiHost + url : ''}`,
          status: 'success'
        });
      }).catch(err => {
        options.onError(err.message);
      });
    };
    return {
      customRequest,
      beforeUpload,
      handleChange,
      handleRemove,
      handlePreview,
      handleDownload,
      loading,
      postData,
      fileList,
      postHeaders,
      actionUrl,
      canUpload
    };
  }
};